// poImportApi.ts — the ONE place every PO-import API-shape assumption lives.
// Mirrors poApi.ts's convention for the PO module.
//
// Backend: api/src/procurement/po-import-router.js + po-import-service.js
// (commit a5f24a2). Two endpoints:
//   POST /procurement/po-import/parse    multipart, writes NOTHING, returns
//                                         staged proposals per document.
//   POST /procurement/po-import/confirm  the reviewed batch, one transaction.
//
// Money is integer cents everywhere, same as poApi.ts — formatCents lives
// there and this module reuses it rather than duplicating it.

import { useApi } from '../apiHelpers';
// The needs-review gate's grammar. Canonical home is components/review/grounding
// (five lanes speak it); used locally by the two shapes below that carry it, and
// re-exported for this lane further down.
import type { ConfirmReviewInput, ReviewRequiredEntry } from '../components/review/grounding';

// ---------------------------------------------------------------------------
// Enums (mirror po-import-service.js IMPORT_STATUSES exactly)
// ---------------------------------------------------------------------------

export const IMPORT_STATUSES = [
  'submitted',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;

export type ImportStatus = (typeof IMPORT_STATUSES)[number];

// draft/pending_approval are deliberately absent — these documents never
// passed through this app's approval control (po-import-service.js comment).
export const IMPORT_STATUS_LABELS: Record<ImportStatus, string> = {
  submitted: 'Submitted',
  partially_received: 'Partially received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export type ImportSource = 'xlsx' | 'pdf_ai' | 'photo_ai';

/**
 * Was this document read by the assistant rather than parsed off a spreadsheet?
 *
 * Mirrors AI_SOURCES in api/src/procurement/po-import-service.js. It decides
 * three things at once, which is why it is one function and not three
 * comparisons: whether the needs-review gate applies, whether the confirm
 * payload must echo the staged snapshot, and whether the screen says a model
 * read this. A lane added later must not be able to slip past one of them.
 */
export const isAiSource = (source: ImportSource | null | undefined): boolean =>
  source === 'pdf_ai' || source === 'photo_ai';

// No 'refused'. The other product's PO number was the only thing that ever
// produced one, and since 2026-08-10 that is a question the reviewer answers
// (`foreign_entity` below) rather than a wall.
export type DocStatus = 'ready' | 'error';

/**
 * What to call each product in the foreign-PO copy.
 *
 * Mirrors PRODUCT_NAME in api/src/procurement/purchasing-entities.js, and must
 * keep mirroring it: the server writes the same sentence into its 409, and two
 * names for one product on one screen reads as two products. Deliberately NOT
 * `instance.displayName` from src/instance.ts, which is the tool's own name
 * ("SwarmDefense.Systems", matching its domain) and only ever describes the
 * deploy you are standing in, never the other one.
 */
export const PRODUCT_LABEL: Record<'firefly' | 'swarm', string> = {
  firefly: 'Firefleet',
  swarm: 'SwarmSystems',
};

/**
 * Which product's purchasing entity does this PO number name?
 *
 * Mirrors `entityForPoNumber` in api/src/procurement/purchasing-entities.js,
 * regexes included. Duplicated across the wire on purpose, and it has to stay in
 * step: the server re-derives the foreign-PO question from the number in the
 * SUBMITTED payload, so a screen that decides from anything else can offer a
 * Confirm the server will refuse.
 */
export const entityForPoNumber = (poNumber: string | null | undefined): 'firefly' | 'swarm' | null => {
  const n = (poNumber || '').trim();
  if (/^PO-SDTEC-/i.test(n)) return 'swarm';
  if (/^PO-FF-TEC/i.test(n)) return 'firefly';
  return null;
};

/**
 * Does this number name the OTHER product, and so need somebody to say yes?
 *
 * Take the number from whatever the reviewer can still EDIT, not from the staged
 * document. In the scan lane the PO number is an input and correcting a misread
 * prefix is a plausible edit, so deciding from `document.foreign_entity` left
 * Confirm enabled on a document the server would 409, with the remedy nowhere on
 * screen.
 */
export const foreignEntityFor = (
  poNumber: string | null | undefined,
  instance: 'firefly' | 'swarm'
): 'firefly' | 'swarm' | null => {
  const named = entityForPoNumber(poNumber);
  return named && named !== instance ? named : null;
};
export type ImportLineType = 'stock' | 'service' | 'charge';

// ---------------------------------------------------------------------------
// Staged shapes — read directly off po-import-service.js's parse() output
// ---------------------------------------------------------------------------

export interface ImportWarning {
  code: string;
  message: string;
  detail?: Record<string, unknown>;
}

export interface ImportLine {
  line_number: number;
  source_row: number | null;
  description: string | null;
  part_number: string | null;
  /**
   * Who MAKES the part, when the document said (migration 036). Carried onto the
   * mint card so a new catalog row is born with it, never onto the purchase order
   * itself, which has no notion of who made a thing.
   *
   * Null is the honest common case: a distributor's paperwork routinely names
   * only itself, and the prompt refuses to fall back to the letterhead.
   */
  manufacturer: string | null;
  quantity: number;
  unit_cost_cents: number | null;
  unit_price_text: string | null;
  unit_price_exact: boolean;
  unit_price_derived: boolean;
  line_total_cents: number | null;
  line_type: ImportLineType;
  line_type_reason: string;
  sku_guess: string | null;
  part_catalog_id: number | null;
  from_totals_block: boolean;
  included: boolean;
}

export interface ImportVendorFields {
  name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface StagedExtraction {
  po_number: string;
  revision: string | null;
  printed_po_number: string | null;
  order_date: string | null;
  quote_number: string | null;
  ship_date: string | null;
  ship_via: string | null;
  terms: string | null;
  ship_to_name: string | null;
  ship_to_attn: string | null;
  ship_to_address: string | null;
  vendor: ImportVendorFields;
  lines: ImportLine[];
  subtotal_cents: number | null;
  printed_subtotal_cents: number | null;
  subtotal_source: 'computed' | 'document';
}

export interface ImportConfidence {
  po_number: number;
  order_date: number;
  quote_number: number;
  ship_date: number;
  ship_via: number;
  terms: number;
  vendor: number;
  ship_to: number;
  lines: number;
  subtotal_cents: number;
}

// ---------------------------------------------------------------------------
// The not-sure gate (po-import-service.js, "Not sure blocks until a human
// reconciles it"). An AI-read document carries an explicit list of places a
// person must look at; confirm() refuses the batch until each one has been
// either edited or affirmatively confirmed. xlsx documents always get [].
//
// FIVE lanes speak the gate's vocabulary now, so its canonical home is
// components/review/grounding.ts. Re-exported below so this lane's pages, its
// draft module and its tests keep reading it off their own contract module.
// Only what this lane actually consumes is re-exported: ReviewReason and
// REVIEW_REASON_LABELS have no reader outside the kit, so they stay there rather
// than growing a facade entry nothing imports.
// ---------------------------------------------------------------------------

export type {
  ReviewTarget,
  ReviewRequiredEntry,
  ReviewResolution,
  ConfirmReviewInput,
} from '../components/review/grounding';
export { reviewKey } from '../components/review/grounding';

export interface WorkbookAppliedCorrection {
  field: string;
  document_value: string | null;
  workbook_value: string;
  reason: string | null;
  evidence?: string | null;
  source: 'register' | 'corrections';
}

export interface WorkbookFlag {
  code: string;
  message: string;
  detail?: Record<string, unknown>;
}

export interface WorkbookOverlay {
  applied_corrections: WorkbookAppliedCorrection[];
  flags: WorkbookFlag[];
}

export interface AttachOfferLine {
  id: string;
  description: string | null;
  sku: string | null;
  part_catalog_id: number | null;
  line_type: ImportLineType;
  quantity_expected: number;
  quantity_received: number;
  unit_cost_cents: number | null;
  status: string;
  vendor: string | null;
  expected_arrival_date: string | null;
}

export interface AttachOffer {
  purchase_order_ref: string;
  target_po_id: string | null;
  lines: AttachOfferLine[];
}

export interface DuplicateInfo {
  existing_po_id: string;
  po_number: string;
  status: string;
  origin: string;
}

export interface AmendmentInfo {
  revision: string;
  base_po_number: string;
  base_in_batch: boolean;
  base_po_id: string | null;
}

export interface VendorSuggestion {
  vendor_id: string;
  name: string;
  overlap: number;
}

export interface VendorCreateProposal {
  name: string;
  normalized_name: string;
  vendor_type: string | null;
  is_disabled: boolean;
  is_disabled_reason: string | null;
  latest_order_date: string | null;
  po_count_in_batch: number;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  payment_terms?: string | null;
}

export interface VendorEnrichment {
  vendor_id: string;
  fields: Record<string, string>;
  from_date?: string | null;
}

export interface VendorResolution {
  matched_vendor_id: string | null;
  matched_name?: string;
  match_kind: 'exact' | 'normalized' | null;
  document_name: string | null;
  create_proposal: VendorCreateProposal | null;
  enrichment: VendorEnrichment | null;
  suggestions: VendorSuggestion[];
}

export interface StagedDocument {
  source_filename: string;
  source: ImportSource | null;
  status: DocStatus;
  error: { code: string; message: string } | null;
  /**
   * The OTHER product's purchasing entity, when this PO number names it.
   *
   * Not an error. Firefly builds a Swarm variant of the Gen2, so Swarm-numbered
   * orders arrive at Firefly's dock, and until 2026-08-10 they were refused
   * outright. Now the document stages normally and confirm holds it until
   * `foreign_instance_ack` says a person meant it.
   */
  foreign_entity: 'firefly' | 'swarm' | null;
  extraction: StagedExtraction | null;
  confidence: ImportConfidence | null;
  warnings: ImportWarning[];
  // Empty for xlsx, and for a pdf_ai document that produced no extraction.
  review_required: ReviewRequiredEntry[];
  workbook_overlay: WorkbookOverlay;
  attach_offer: AttachOffer | null;
  vendor_resolution: VendorResolution | null;
  duplicate: DuplicateInfo | null;
  amendment: AmendmentInfo | null;
}

export interface WorkbookSummary {
  present: boolean;
  source_filename?: string;
  counts?: {
    register_rows: number;
    corrections: number;
    open_questions: number;
    pos_with_open_questions: number;
  };
  unattached_questions?: Array<{ category: string | null; ref: string | null; question: string | null }>;
  warnings?: ImportWarning[];
  error?: { code: string; message: string };
}

export interface ParseResponse {
  instance: 'firefly' | 'swarm';
  instance_po_prefix: string;
  today: string;
  workbook: WorkbookSummary;
  documents: StagedDocument[];
  summary: {
    files: number;
    ready: number;
    failed: number;
    with_attach_offers: number;
    duplicates: number;
    new_vendors: number;
    /** How many name the other product, and so need a person to say yes. */
    foreign_entity: number;
  };
}

// ---------------------------------------------------------------------------
// Confirm payload
// ---------------------------------------------------------------------------

export interface ConfirmLineInput {
  quantity: number;
  line_type: ImportLineType;
  unit_cost_cents: number | null;
  part_catalog_id: number | null;
  description: string | null;
  // Echoed through so the server can build the "[imported unit price X,
  // rounded to Y cents]" note automatically — see po-import-service.js
  // _validateDocument. Omit for lines that never had a sub-cent price.
  unit_price_text?: string | null;
  unit_price_exact?: boolean;
  included?: boolean;
}

export interface ConfirmVendorCreate {
  name: string;
  type: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
  payment_terms?: string | null;
  is_disabled?: boolean;
}

export interface ConfirmDocumentInput {
  source_filename: string;
  source: ImportSource;
  po_number: string;
  status: ImportStatus;
  order_date: string;
  quote_number?: string | null;
  ship_date?: string | null;
  ship_via?: string | null;
  terms?: string | null;
  ship_to_name?: string | null;
  ship_to_attn?: string | null;
  ship_to_address?: string | null;
  notes?: string | null;
  subtotal_cents?: number;
  vendor_id?: string | null;
  vendor_create?: ConfirmVendorCreate | null;
  vendor_name?: string | null;
  vendor_enrichment?: { vendor_id: string; fields: Record<string, string> } | null;
  lines: ConfirmLineInput[];
  attach_line_ids: string[];
  /**
   * "Yes, I meant to track the other product's purchase order here."
   *
   * Required by the server whenever the PO number names the other entity, and
   * derived there from the NUMBER rather than from anything echoed back, so this
   * flag can only ever say yes to a question the server already asked.
   */
  foreign_instance_ack?: boolean;
  amended_from_po_number?: string | null;
  applied_corrections?: unknown[];
  workbook_flags?: unknown[];
  // pdf_ai docs only — the UNEDITED parse() extraction, verbatim, so the
  // server can compute the AI-learning delta against what the reviewer
  // actually confirmed. Never the reviewer's edited draft.
  staged_extraction?: StagedExtraction;
  // pdf_ai docs only, and echoed for ONE reason: confirm() recomputes
  // review_required from these three rather than trusting a client-supplied
  // list, so a payload cannot shorten its own homework.
  staged_confidence?: ImportConfidence | null;
  staged_warnings?: ImportWarning[];
  // One entry per PLACE the reader was unsure of (reviewKey), saying the person
  // either changed it or looked at it and left it. A pdf_ai document with any
  // unanswered entry is refused with `unreconciled_ai_fields`.
  reviews?: ConfirmReviewInput[];
}

export interface ConfirmCreatedRow {
  source_filename: string;
  po_number: string;
  purchase_order_id: string;
  line_count: number;
  vendor_id: string;
  attached_line_count?: number;
}

export interface ConfirmSkippedRow {
  source_filename: string;
  po_number: string;
  purchase_order_id: string;
  reason: string;
  attached_line_count?: number;
}

export interface ConfirmResult {
  ok: true;
  created: ConfirmCreatedRow[];
  skipped: ConfirmSkippedRow[];
  attached: number;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    po_number?: string;
    index?: number;
    line_id?: string;
    [key: string]: unknown;
  };
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

export function usePoImportApi() {
  const api = useApi();

  return {
    parse: (files: File[], workbook: File | null): Promise<ParseResponse> => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (workbook) fd.append('workbook', workbook);
      return api.post('/procurement/po-import/parse', { body: fd });
    },

    confirm: (documents: ConfirmDocumentInput[]): Promise<ConfirmResult> =>
      api.post('/procurement/po-import/confirm', { body: { documents } }),
  };
}

// ---------------------------------------------------------------------------
// Vendor-name normalization, client side
// ---------------------------------------------------------------------------
//
// Mirrors po-import-service.js normalizeVendorName EXACTLY (same regex, same
// order) so a name typed here for a nameless document groups the same way a
// server-side proposal would. This is a grouping aid only — the server is the
// authority on the real create_proposal.normalized_name for documents that
// already named a vendor; this function is only ever run on FE-only input
// (a manually typed name, or an existing-vendor containment check).
export function normalizeVendorName(name: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/\b(inc|llc|l l c|ltd|limited|corp|corporation|co|company|gmbh|bv|sa|ag|plc|llp|lp|aps|as)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

// ---------------------------------------------------------------------------
// Confidence display — MOVED to components/review/confidence, alongside the dot
// that renders it, now that all five reader lanes stage a confidence map. Same
// tiers, same colors. Re-exported here so this lane's card keeps reading its
// vocabulary off its own contract module, the way it reads the review grammar.
// ---------------------------------------------------------------------------

export type { ConfidenceTier } from '../components/review/confidence';
export {
  averageConfidence,
  confidenceTier,
  CONFIDENCE_DOT_COLORS,
} from '../components/review/confidence';

// ---------------------------------------------------------------------------
// Money helpers — MOVED to poApi.ts, alongside formatCents, when the PO detail
// page grew an editor and became a second caller. Re-exported here so this
// module's existing importers keep working; both lanes must round money the
// same way or a cost typed in one place and read in the other disagrees by a
// cent.
// ---------------------------------------------------------------------------

export { dollarsToCents, centsToDollarsInput } from './poApi';
